import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, MapPin, Building, Clock, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// --- Service Card ---
interface ServiceCardProps {
  title: string;
  description: string;
  link: string;
  icon?: React.ReactNode;
}
export function ServiceCard({
  title,
  description,
  link,
  icon
}: ServiceCardProps) {
  return <Link to={link} className="group block h-full" data-id="element-38">
      <div className="bg-white p-8 rounded-2xl h-full border border-warmgray/50 shadow-sm hover:shadow-premium transition-all duration-300 flex flex-col" data-id="element-39">
        {icon && <div className="text-accent mb-6 bg-offwhite w-14 h-14 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-40">
            {icon}
          </div>}
        <h3 className="text-xl font-medium mb-3 text-charcoal" data-id="element-41">{title}</h3>
        <p className="text-charcoal/70 text-sm leading-relaxed mb-6 flex-grow" data-id="element-42">
          {description}
        </p>
        <div className="flex items-center text-accent text-sm font-medium mt-auto" data-id="element-43">
          Lees meer
          <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-44" />
        </div>
      </div>
    </Link>;
}
// --- Step Card ---
interface StepCardProps {
  number: string;
  title: string;
  description: string;
}
export function StepCard({
  number,
  title,
  description
}: StepCardProps) {
  return <div className="relative pl-6 border-l-2 border-warmgray/60 h-full py-2" data-id="element-45">
      <div className="absolute -left-[17px] top-2 w-8 h-8 bg-charcoal text-white rounded-full flex items-center justify-center text-sm font-semibold" data-id="element-46">
        {number}
      </div>
      <h3 className="text-lg font-medium mb-2 text-charcoal" data-id="element-47">{title}</h3>
      <p className="text-charcoal/60 text-sm leading-relaxed" data-id="element-48">{description}</p>
    </div>;
}
// --- Blog Card ---
interface BlogCardProps {
  title: string;
  excerpt: string;
  slug: string;
  cluster: string;
  image: string;
  date: string;
  readTime: string;
}
export function BlogCard({
  title,
  excerpt,
  slug,
  cluster,
  image,
  date,
  readTime
}: BlogCardProps) {
  return <Link to={`/kennisbank/${cluster}/${slug}`} className="group block h-full" data-id="element-49">
      <div className="bg-white rounded-2xl overflow-hidden border border-warmgray/50 shadow-sm hover:shadow-premium transition-all duration-300 h-full flex flex-col" data-id="element-50">
        <div className="relative h-48 overflow-hidden bg-warmgray" data-id="element-51">
          <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-52" />
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-charcoal uppercase tracking-wider" data-id="element-53">
            {cluster.replace('-', ' ')}
          </div>
        </div>
        <div className="p-6 flex flex-col flex-grow" data-id="element-54">
          <div className="flex items-center gap-4 text-xs text-charcoal/50 mb-3" data-id="element-55">
            <span className="flex items-center gap-1" data-id="element-56">
              <Calendar size={14} data-id="element-57" /> {date}
            </span>
            <span className="flex items-center gap-1" data-id="element-58">
              <Clock size={14} data-id="element-59" /> {readTime}
            </span>
          </div>
          <h3 className="text-lg font-medium mb-3 text-charcoal group-hover:text-accent transition-colors line-clamp-2" data-id="element-60">
            {title}
          </h3>
          <p className="text-charcoal/70 text-sm leading-relaxed mb-6 flex-grow line-clamp-3" data-id="element-61">
            {excerpt}
          </p>
          <div className="flex items-center text-accent text-sm font-medium mt-auto" data-id="element-62">
            Lees artikel
            <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-63" />
          </div>
        </div>
      </div>
    </Link>;
}
// --- Adviseur Card ---
interface AdviseurCardProps {
  name: string;
  company: string;
  location: string;
  slug: string;
  image: string;
}
export function AdviseurCard({
  name,
  company,
  location,
  slug,
  image
}: AdviseurCardProps) {
  return <Link to={`/adviseurs/${slug}`} className="group block h-full" data-id="element-64">
      <div className="bg-white rounded-2xl overflow-hidden border border-warmgray/50 shadow-sm hover:shadow-premium transition-all duration-300 h-full flex flex-col" data-id="element-65">
        <div className="h-64 overflow-hidden bg-warmgray relative" data-id="element-66">
          <img src={image} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-67" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-68" />
        </div>
        <div className="p-6 flex flex-col flex-grow text-center items-center" data-id="element-69">
          <h3 className="text-xl font-medium mb-1 text-charcoal" data-id="element-70">{name}</h3>
          <div className="flex items-center gap-1 text-sm text-charcoal/60 mb-2" data-id="element-71">
            <Building size={14} data-id="element-72" />
            <span data-id="element-73">{company}</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-charcoal/60 mb-6" data-id="element-74">
            <MapPin size={14} data-id="element-75" />
            <span data-id="element-76">{location}</span>
          </div>
          <div className="mt-auto w-full py-2 border border-warmgray rounded-full text-sm font-medium text-charcoal group-hover:bg-accent group-hover:text-white group-hover:border-accent transition-colors" data-id="element-77">
            Bekijk profiel
          </div>
        </div>
      </div>
    </Link>;
}
// --- FAQ Accordion ---
interface FAQAccordionProps {
  question: string;
  answer: string;
}
export function FAQAccordion({
  question,
  answer
}: FAQAccordionProps) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-warmgray/50 last:border-0" data-id="element-78">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} data-id="element-79">
        <span className="text-lg font-medium text-charcoal group-hover:text-accent transition-colors pr-8" data-id="element-80">
          {question}
        </span>
        <span className={`w-8 h-8 rounded-full bg-offwhite flex items-center justify-center text-charcoal transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180 bg-accent text-white' : ''}`} data-id="element-81">
          <ChevronDown size={18} data-id="element-82" />
        </span>
      </button>
      <AnimatePresence data-id="element-83">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-84">
            <div className="pb-6 text-charcoal/70 leading-relaxed" data-id="element-85">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}